/**
 * Mock Role Banner Component
 * 
 * Displays a banner at the top of the app when a mock role is active.
 */

import React from 'react';
import { useRoleSwitch } from '../../hooks/useRoleSwitch';
import { useQuery } from '@tanstack/react-query';
import { getRole, type RoleDetail } from '../../api/rbac';
import { Button, Badge } from '../UI';
import { AlertTriangle, X } from 'lucide-react';

export const MockRoleBanner: React.FC = () => {
  const { mockRoleId, clearMockRole, isMockRoleActive } = useRoleSwitch();

  // Fetch mock role details
  const { data: mockRole } = useQuery<RoleDetail>({
    queryKey: ['role', mockRoleId, 'details'],
    queryFn: () => getRole(mockRoleId!),
    enabled: isMockRoleActive && !!mockRoleId,
  });

  if (!isMockRoleActive || !mockRole) {
    return null;
  }

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-4 py-2">
      <div className="container mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-yellow-900 dark:text-yellow-100">
              Viewing as:
            </span>
            <Badge variant="warning" className="text-xs">
              {mockRole.display_name || mockRole.name}
            </Badge>
            <span className="text-xs text-yellow-700 dark:text-yellow-300">
              (Mock Role - All permission checks use this role)
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearMockRole}
          className="text-yellow-900 dark:text-yellow-100 hover:bg-yellow-100 dark:hover:bg-yellow-900/40"
        >
          <X className="w-4 h-4 mr-1" />
          Exit Mock View
        </Button>
      </div>
    </div>
  );
};


